import React from 'react';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons';
import { iconOptions } from '@frontend/app/containers/Projects/CampaignPage/components/ImageIconSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import xss from 'xss';
import { ViewMode } from '../../constants';

interface Perk {
  text: string;
  icon?: string;
}

interface PerksProps {
  view: ViewMode;
  title?: string;
  perks: Perk[];
  listStyleType?: 'number' | 'bullet' | 'icon' | 'none';
  backgroundColor?: string;
  titleColor?: string;
  titleFontSize?: number;
  titleFontFamily?: string;
  bodyTextColor?: string;
  bodyTextFontSize?: number;
  bodyTextFontFamily?: string;
}

export function Perks({
  view,
  title = 'Program Perks',
  perks,
  listStyleType = 'number',
  backgroundColor = '#b28d7b',
  titleColor = 'white',
  titleFontSize = 28,
  titleFontFamily = 'Inter, sans-serif',
  bodyTextColor = 'white',
  bodyTextFontSize = 18,
  bodyTextFontFamily = 'Inter, sans-serif',
}: PerksProps) {
  const titleStyle = {
    color: titleColor,
    fontSize: titleFontSize,
    fontFamily: titleFontFamily,
    fontWeight: 600,
  };

  const bodyStyle = {
    color: bodyTextColor,
    fontSize: bodyTextFontSize,
    fontFamily: bodyTextFontFamily,
  };

  function renderPrefix(idx: number, perk: Perk) {
    switch (listStyleType) {
      case 'number':
        return (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white text-black font-semibold">
            {idx + 1}
          </span>
        );
      case 'bullet':
        return (
          <span className="text-base font-bold mr-2" style={{ color: bodyTextColor }}>
            •
          </span>
        );
      case 'icon': {
        const iconObj = iconOptions.find((icon) => icon.name === perk.icon);
        const iconToUse = iconObj ? iconObj.icon : faBriefcase;
        return (
          <FontAwesomeIcon
            icon={iconToUse}
            className="w-5 h-5 align-middle text-grey-3"
          />
        );
      }
      case 'none':
      default:
        return null;
    }
  }

  const isDesktop = view === ViewMode.DESKTOP;

  return (
    <section className="w-full py-12 px-4" style={{ background: backgroundColor }}>
      <div
        className="mb-8 text-center w-full break-words"
        style={{ ...titleStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      >
        {title}
      </div>
      {isDesktop ? (
        <div className="flex flex-row justify-start gap-16 items-start w-full">
          {perks.map((perk, idx) => (
            <div key={idx} className="flex-1 flex items-start gap-4 max-w-md w-full">
              {renderPrefix(idx, perk)}
              <span
                className="text-base font-normal w-full break-words"
                style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: xss(perk.text) }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-6 items-center w-full">
          {perks.map((perk, idx) => (
            <div key={idx} className="flex items-start gap-4 w-full max-w-xs">
              {renderPrefix(idx, perk)}
              <span
                className="text-base font-normal w-full break-words overflow-x-hidden"
                style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: xss(perk.text) }}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
